import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CompaniesService } from 'src/app/services/companies.service';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { SelectableCompany } from 'src/app/models/company.model';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
// import { UserPermissionDirective } from 'src/app/directives/user-permissions.directive';
import { JobMonitorComponent } from 'src/app/components/job-monitor/job-monitor.component';
import { UserAttributesCacheService } from 'src/app/services/user-attributes-cache.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { RolePermissionsService } from 'src/app/services/role-permissions.service';
import { StateParamService } from 'src/app/services/state-param.service';
import { SessionCleanupService } from 'src/app/services/session-cleanup.service';
import { StatePreservingRouterLinkDirective } from 'src/app/directives/state-preserving-router-link.directive';
import { signOut } from 'aws-amplify/auth';
import { MessagingMapService } from 'src/app/services/messaging-map.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    ToastModule,
    TooltipModule,
    JobMonitorComponent,
    StatePreservingRouterLinkDirective
  ],
    providers: [MessageService]
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  companies: SelectableCompany[] = [];
  selectedCompany: SelectableCompany | null = null;
  isLoading = false;

  error: string | null = null;
  canManageCompanies = false;
  canManageUsers = false;
  /** Messaging Map nav item; only shown for allowlisted companies. */
  showMessagingMap = false;

  // Mobile navigation state
  sidebarVisible = false;
  isMobile = false;

  // Touch gesture properties
  private touchStartX = 0;
  private touchStartY = 0;
  private touchEndX = 0;
  private touchEndY = 0;

  constructor(
    private router: Router,
    private messageService: MessageService,
    private companiesService: CompaniesService,
    private userAttributesCache: UserAttributesCacheService,
    private onboardingService: OnboardingService,
    private rolePermissionsService: RolePermissionsService,
    private stateParamService: StateParamService,
    private sessionCleanup: SessionCleanupService,
    private messagingMapService: MessagingMapService
  ) {
    // Initialize state parameter extraction on app startup
    this.stateParamService.initializeFromRoute(this.router.routerState.root);
  }

  async ngOnInit(): Promise<void> {
    // Check if mobile on init
    this.checkIfMobile();

    // Subscribe to loading states from both services
    combineLatest([
      this.companiesService.isLoading$,
      this.userAttributesCache.isLoading$
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([companiesLoading, attributesLoading]) => {
      this.isLoading = companiesLoading || attributesLoading;
    });

    // Subscribe to available companies
    this.companiesService.availableCompanies$
      .pipe(takeUntil(this.destroy$))
      .subscribe(companies => {
        this.companies = companies;
      });

    // Subscribe to selected company changes
    this.companiesService.selectedCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        this.selectedCompany = company;
        this.updatePermissions();
        this.updateMessagingMapNav(company);
      });

    // Initialize company data with optimized loading
    try {
      await this.initializeCompaniesOptimized();

      // Start onboarding tour if appropriate
      this.startOnboardingIfNeeded();
    } catch (error) {
      console.error('Error initializing companies:', error);
      this.error = 'Unable to load company data. Please continue to create a company by clicking the Company Management button.';

      // Show onboarding tour for new users even if company loading failed
      this.startOnboardingIfNeeded();
    }
  }

  private async updateMessagingMapNav(company: SelectableCompany | null): Promise<void> {
    this.showMessagingMap = false;
    if (!company) {
      return;
    }
    const enabled = await this.messagingMapService.isEnabled(company.companyId);
    // Ignore the answer if the user switched company while it was in flight
    if (this.selectedCompany?.companyId === company.companyId) {
      this.showMessagingMap = enabled;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeCompaniesOptimized(): Promise<void> {
    try {
      // Use cached user attributes to get primary company ID
      const userAttributes = await this.userAttributesCache.getUserAttributes();
      const primaryCompanyId = userAttributes.primaryCompanyId;

      // Check if user has a primary company ID, if not they may need to create a company
      if (!primaryCompanyId) {
        console.log('No primary company ID found, user may need to create a company');
        // Don't return here - still try to load companies as they might have access through other means
      }

      // Parallel execution: Check if companies are loaded and initialize if needed
      const [hasCompanies] = await Promise.all([
        Promise.resolve(this.companiesService.hasCompaniesLoaded()),
        // Pre-warm user attributes cache if not already loaded
        this.userAttributesCache.getUserAttributes()
      ]);

      if (!hasCompanies) {
        await this.companiesService.initializeCompanySelection();
      }

      // Set selected company using cached primary company ID
      await this.setSelectedCompanyFromCache();
    } catch (error) {
      console.error('Error in initializeCompaniesOptimized:', error);
      // Don't throw the error - let the user see the create company option
      this.error = 'Unable to load company data. Please continue to create a company by clicking the Company Management button.';
    }
  }

  private async setSelectedCompanyFromCache(): Promise<void> {
    try {
      // Get primary company ID from cache (much faster than Cognito)
      const primaryCompanyId = this.userAttributesCache.getPrimaryCompanyId();

      if (primaryCompanyId && this.companies.length > 0) {
        // Find the company with the matching ID
        const primaryCompany = this.companies.find(company =>
          company.companyId === primaryCompanyId
        );

        if (primaryCompany) {
          // Set the selected company in the service
          this.companiesService.setSelectedCompany(primaryCompany);
          console.log('Selected company set from cache:', primaryCompany.companyName);
        } else {
          console.warn('Primary company ID from cache not found in available companies:', primaryCompanyId);
          // Optionally set first available company as fallback
          if (this.companies.length > 0) {
            await this.setAndUpdatePrimaryCompanyOptimized(this.companies[0]);
          }
        }
      } else if (this.companies.length > 0) {
        // No primary company set, set the first available company
        await this.setAndUpdatePrimaryCompanyOptimized(this.companies[0]);
      }
    } catch (error) {
      console.error('Error setting selected company from cache:', error);
      // Fallback to first company if available
      if (this.companies.length > 0) {
        this.companiesService.setSelectedCompany(this.companies[0]);
      }
    }
  }

  private async setAndUpdatePrimaryCompanyOptimized(company: SelectableCompany): Promise<void> {
    try {
      // Set the selected company in the service immediately for better UX
      this.companiesService.setSelectedCompany(company);

      // Update both Cognito and cache in parallel (non-blocking)
      this.companiesService.updatePrimaryCompanyInCognito(company.companyId)
        .catch(error => {
          console.error('Error updating primary company in background:', error);
          // Don't throw here to avoid blocking the UI
        });

      console.log('Primary company set:', company.companyName);
    } catch (error) {
      console.error('Error setting primary company:', error);
      throw error;
    }
  }

  private async updatePrimaryCompanyInCognito(companyId: string): Promise<void> {
    // Delegate to the optimized method in companies service
    return this.companiesService.updatePrimaryCompanyInCognito(companyId);
  }

  async onCompanyChange(event: any): Promise<void> {
    const selectedCompany = event.value;
    if (selectedCompany) {
      try {
        // Set the selected company in the service immediately
        this.companiesService.setSelectedCompany(selectedCompany);
        this.showCompanyChangeToast(selectedCompany)
        // Update Cognito in the background (non-blocking for better UX)
        this.updatePrimaryCompanyInCognito(selectedCompany.companyId)
          .catch(error => {
            console.error('Error updating company selection in background:', error);
            this.error = 'Company selected, but failed to save preference. It will reset on next login.';
          });

        console.log('Company selection changed to:', selectedCompany.companyName);
      } catch (error) {
        console.error('Error updating company selection:', error);
        this.error = 'Failed to update company selection. Please try again.';
      }
    }
  }

  private showCompanyChangeToast(selectedCompany:any): void {
      // this.messageService.add({
      //   severity: 'info',
      //   summary: 'Company Changed',
      //   detail: `Now viewing email data for ${selectedCompany.companyName}`,
      //   life: 3000
      // });
 
  }

  async refreshCompanies(): Promise<void> {
    try {
      this.error = null;
      
      // Refresh both company data and user attributes cache
      await Promise.all([
        this.companiesService.refreshCompanyData(),
        this.userAttributesCache.refreshAttributes()
      ]);

      // After refresh, ensure the selected company matches cached data
      await this.setSelectedCompanyFromCache();
    } catch (error) {
      console.error('Error refreshing companies:', error);
      this.error = 'Failed to refresh company data.';
    }
  }

  /**
   * Update UI permissions based on current user role
   */
  private updatePermissions(): void {
    this.canManageCompanies = this.rolePermissionsService.canCurrentUserManageCompanies();
    this.canManageUsers = this.rolePermissionsService.canCurrentUserManageUsers();
  }

  // Helper method to get company type icon
  getCompanyIcon(company: SelectableCompany): string {
    return company.companyType === 'parent' ? 'pi-building' : 'pi-briefcase';
  }

  // Helper method to get company type color
  getCompanyIconColor(company: SelectableCompany): string {
    return company.companyType === 'parent' ? '#3b82f6' : '#10b981';
  }

  clearError(): void {
    this.error = null;
  }

  async logout(event: Event) {
    event.preventDefault();
    try {
      // SECURITY: clear ALL user-scoped client state through the single shared path.
      // This used to clear only user_attributes_cache + the company subjects, leaving
      // companies_data_cache / ag_setup_progress_cache / userSettings_* behind for the
      // next person to use the browser. Keep this in sync with AuthService.signOut().
      this.sessionCleanup.clearAllClientState();

      // Use the direct signOut function from aws-amplify/auth with global: true
      // This properly invalidates tokens on both client and server
      await signOut({ global: true });

      // Navigate to login page
      this.router.navigate(['/login'], { queryParamsHandling: 'merge' });
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if sign out fails, clear local state and redirect.
      // SECURITY: the error path must clear exactly as much as the success path.
      this.sessionCleanup.clearAllClientState();
      this.router.navigate(['/login'], { queryParamsHandling: 'merge' });
    }
  }

  /**
   * Start onboarding tour if conditions are met
   */
  private startOnboardingIfNeeded(): void {
    // Small delay to ensure DOM is fully rendered
    setTimeout(() => {
      // Check if user has no companies or is new
      const hasNoCompanies = this.companies.length === 0;
      const shouldShowTour = this.onboardingService.shouldShowTour();
      
      if (hasNoCompanies || shouldShowTour) {
        console.log('Starting onboarding tour');
        this.onboardingService.startCompanySetupTour();
      }
    }, 1000);
  }

  /**
   * Manually start the onboarding tour (for help menu)
   */
  startOnboardingTour(): void {
    this.onboardingService.startTourManually();
  }

  /**
   * Reset onboarding state (for testing)
   */
  resetOnboarding(): void {
    this.onboardingService.resetTourState();
    console.log('Onboarding state reset - tour will show on next refresh');
  }

  /**
   * Mobile navigation methods
   */
  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkIfMobile();
    if (!this.isMobile) {
      this.sidebarVisible = false; // Auto-close sidebar when switching to desktop
    }
  }

  private checkIfMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  closeSidebar(): void {
    this.sidebarVisible = false;
  }

  onSidebarLinkClick(): void {
    // Close sidebar when a navigation link is clicked on mobile
    if (this.isMobile) {
      this.closeSidebar();
    }
  }

  /**
   * Touch gesture handlers for mobile navigation
   */
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.isMobile) return;

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (!this.isMobile) return;

    this.touchEndX = event.changedTouches[0].clientX;
    this.touchEndY = event.changedTouches[0].clientY;

    this.handleSwipeGesture();
  }

  private handleSwipeGesture(): void {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const minSwipeDistance = 50; // Minimum swipe distance in pixels
    const maxVerticalDistance = 100; // Maximum vertical movement to still count as horizontal swipe

    // Check if this is a horizontal swipe
    if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaY) < maxVerticalDistance) {

      // Swipe right to open sidebar (only if sidebar is closed and swipe starts from left edge)
      if (deltaX > 0 && this.touchStartX < 50 && !this.sidebarVisible) {
        this.toggleSidebar();
      }

      // Swipe left to close sidebar (only if sidebar is open)
      if (deltaX < 0 && this.sidebarVisible) {
        this.closeSidebar();
      }
    }
  }

  /**
   * Handle touch events on sidebar overlay to close sidebar
   */
  onSidebarOverlayTouch(event: TouchEvent): void {
    // Prevent event propagation to avoid conflicts
    event.stopPropagation();
    this.closeSidebar();
  }
}